import { PenaltyType } from "../../types/models";

/**
 * The calls a scorekeeper reaches for most often, in the order they are shown
 * as buttons. The API's penalty type list runs to around sixty entries, nearly
 * all of them majors, matches and misconducts that come up a handful of times a
 * season — putting every one on screen as a button buries the dozen that are
 * used every game.
 *
 * Matched against `PenaltyType.name`. The list is global reference data seeded
 * by the API, so these names are stable; a name that does not match anything
 * simply contributes no button, and every call stays reachable in the full list
 * underneath.
 */
export const COMMON_PENALTY_NAMES: string[] = [
  "Tripping",
  "Hooking",
  "Slashing",
  "Holding",
  "Interference",
  "Roughing",
  "Cross-Checking",
  "High-Sticking",
  "Elbowing",
  "Boarding",
  "Delay of Game",
  "Too Many Men",
];

export interface SplitPenaltyTypes {
  /** The common calls, ordered as COMMON_PENALTY_NAMES lists them. */
  common: PenaltyType[];
  /** Everything else, left in the order the API returned it. */
  rest: PenaltyType[];
}

export function splitPenaltyTypes(types: PenaltyType[]): SplitPenaltyTypes {
  const rank = new Map(
    COMMON_PENALTY_NAMES.map((name, index) => [name, index] as const),
  );

  const common: PenaltyType[] = [];
  const rest: PenaltyType[] = [];

  types.forEach((type) => {
    if (rank.has(type.name)) {
      common.push(type);
    } else {
      rest.push(type);
    }
  });

  common.sort(
    (a, b) =>
      (rank.get(a.name) ?? Number.MAX_SAFE_INTEGER) -
      (rank.get(b.name) ?? Number.MAX_SAFE_INTEGER),
  );

  return { common, rest };
}
